import React, { useState, useMemo } from 'react';
import { Card, Segmented, Empty, Space, Typography } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';

const { Text } = Typography;

const CourseWatchChart = ({ records }) => {
  const [period, setPeriod] = useState('Day');

  const chartData = useMemo(() => {
    if (!records || records.length === 0) return [];

    const grouped = {};
    const parseDuration = (durStr) => {
      if (!durStr) return 0;
      const parts = durStr.split(':');
      if (parts.length === 3) {
        return parseInt(parts[0]) * 60 + parseInt(parts[1]) + parseInt(parts[2]) / 60;
      }
      return 0;
    };

    const getWeek = (date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
      return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
    };

    records.forEach(r => {
      if (!r.start_time) return;
      const d = new Date(r.start_time);
      if (isNaN(d.getTime())) return;

      let key;
      if (period === 'Day') {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else if (period === 'Week') {
        key = `${d.getFullYear()}-W${String(getWeek(d)).padStart(2, '0')}`;
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!grouped[key]) {
        grouped[key] = { key, LIVE: 0, VOD: 0 };
      }
      const dur = parseDuration(r.video_duration);
      if (r.video_type === 'LIVE') {
        grouped[key].LIVE += dur;
      } else {
        grouped[key].VOD += dur;
      }
    });

    const sorted = Object.values(grouped).sort((a, b) => a.key.localeCompare(b.key));
    return sorted;
  }, [records, period]);

  if (chartData.length === 0) {
    return null;
  }

  const svgWidth = 800;
  const svgHeight = 300;
  const padding = 40;

  const maxLive = Math.max(...chartData.map(d => d.LIVE), 0);
  const maxVod = Math.max(...chartData.map(d => d.VOD), 0);
  const maxVal = Math.max(maxLive, maxVod, 10); // at least 10 minutes max for scale

  const getX = (index) => {
    if (chartData.length <= 1) return svgWidth / 2;
    return padding + (index / (chartData.length - 1)) * (svgWidth - 2 * padding);
  };

  const getY = (val) => {
    return svgHeight - padding - (val / maxVal) * (svgHeight - 2 * padding);
  };

  const makePath = (type) => {
    return chartData.reduce((acc, d, i) => {
      const x = getX(i);
      const y = getY(d[type]);
      return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
    }, '');
  };

  return (
    <Card
      title={
        <Space>
          <LineChartOutlined style={{ color: '#10b981', fontSize: 18 }} />
          <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>Watch Duration Trend (Minutes)</Text>
        </Space>
      }
      extra={
        <Segmented
          options={['Day', 'Week', 'Month']}
          value={period}
          onChange={setPeriod}
        />
      }
      style={{
        borderRadius: 16,
        marginBottom: 24,
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 16 }}>
        <Space>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          <Text style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>LIVE Watch (mins)</Text>
        </Space>
        <Space>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#8b5cf6', display: 'inline-block' }} />
          <Text style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>VOD Watch (mins)</Text>
        </Space>
      </div>

      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: 'auto', minWidth: 600 }}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
            const y = padding + pct * (svgHeight - 2 * padding);
            return (
              <g key={i}>
                <line x1={padding} y1={y} x2={svgWidth - padding} y2={y} stroke="rgba(150, 150, 150, 0.2)" strokeDasharray="4 4" />
                <text x={padding - 5} y={y + 4} textAnchor="end" fontSize={10} fill="var(--text-secondary, #9ca3af)">
                  {Math.round(maxVal - (pct * maxVal))}
                </text>
              </g>
            );
          })}

          {/* Lines */}
          <path d={makePath('LIVE')} fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <path d={makePath('VOD')} fill="none" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

          {/* Points & X-Axis Labels */}
          {chartData.map((d, i) => {
            const x = getX(i);
            const yLive = getY(d.LIVE);
            const yVod = getY(d.VOD);
            // Show fewer labels if too many points
            const showLabel = chartData.length <= 15 || i % Math.ceil(chartData.length / 10) === 0 || i === chartData.length - 1;

            return (
              <g key={i}>
                <circle cx={x} cy={yLive} r={4} fill="#ef4444" stroke="#fff" strokeWidth={2} />
                <circle cx={x} cy={yVod} r={4} fill="#8b5cf6" stroke="#fff" strokeWidth={2} />
                {showLabel && (
                  <text x={x} y={svgHeight - 10} textAnchor="middle" fontSize={10} fill="var(--text-secondary, #6b7280)" transform={chartData.length > 15 ? `rotate(-45 ${x} ${svgHeight - 15})` : ''}>
                    {d.key}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </Card>
  );
};

export default CourseWatchChart;
